'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useTRPC } from '@/lib/trpc/provider';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Gift, X } from 'lucide-react';
import { PaymentMethodSelect, usePaymentMethod } from '@/components/payments/PaymentMethodSelect';
import { redirectToCheckout } from '@/lib/payments/redirect-to-checkout';

type GiftVoucherDialogProps = {
  courseId: string;
  courseName: string;
  priceCents: number;
  open: boolean;
  onClose: () => void;
};

export function GiftVoucherDialog({
  courseId,
  courseName,
  priceCents,
  open,
  onClose,
}: GiftVoucherDialogProps) {
  const trpc = useTRPC();
  const t = useTranslations('giftVoucher');
  const tCommon = useTranslations('common');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const { method, setMethod } = usePaymentMethod();

  const purchaseMutation = useMutation(
    trpc.giftVoucher.purchaseGiftVoucher.mutationOptions({
      onSuccess: (data) => {
        redirectToCheckout(data.redirect);
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );

  const handleClose = () => {
    setRecipientEmail('');
    setPersonalMessage('');
    onClose();
  };

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipientEmail) {
      toast.error(t('enterRecipientEmail'));
      return;
    }
    purchaseMutation.mutate({
      courseId,
      recipientEmail,
      personalMessage: personalMessage || undefined,
      method,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-[#fc800a]" />
            <h2 className="text-lg font-semibold text-[#1e306e]">{t('giftThisCourse')}</h2>
          </div>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 p-3 rounded-lg bg-[#f9fafb] border border-[#e5e7eb]">
          <p className="text-sm font-medium text-[#374151]">{courseName}</p>
          <p className="text-sm text-gray-500">${(priceCents / 100).toFixed(2)}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('recipientEmail')} *
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              required
              maxLength={255}
              className="w-full px-3 py-2 text-sm border border-[#e5e7eb] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e306e]/20"
              placeholder="recipient@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('personalMessage')} <span className="text-gray-400">({tCommon('optional')})</span>
            </label>
            <textarea
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-[#e5e7eb] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e306e]/20"
              placeholder={t('personalMessagePlaceholder')}
            />
            <p className="text-xs text-gray-400 mt-1">{personalMessage.length}/500</p>
          </div>
          <PaymentMethodSelect value={method} onChange={setMethod} disabled={purchaseMutation.isPending} />
          <button
            type="submit"
            disabled={purchaseMutation.isPending}
            className="w-full py-2.5 text-sm font-medium text-white bg-[#fc800a] rounded-lg hover:bg-[#fc800a]/90 transition-colors disabled:opacity-50"
          >
            {purchaseMutation.isPending ? `${tCommon('processing')}...` : t('purchaseGift', { amount: (priceCents / 100).toFixed(2) })}
          </button>
        </form>
      </div>
    </div>
  );
}
